import './aiPhotoPaywallContent.scss'
import React from 'react'
import { registerPaymentContent } from '../../components/VipPaymentModal/contentRegistry'
import CONFIG from '../../../constants/config'

/**
 * AI 问照片「开通会员弹窗」定制内容。
 *
 * VipPaymentModal 是全站共享组件,默认显示「钱钱钱」。这里通过注册表机制
 * (registerPaymentContent)给它注入 AI 问照片专属的标题 / 副标题 / 各档积分小字 /
 * 各档横幅,打开时由 openVipByKind 的第 4 参 customContent 传 { variant, data } 触发。
 * 不改弹窗本体。
 */

/** variant 标识(全局唯一,与打开时传的 variant 对应) */
export const AI_PHOTO_PAYWALL_VARIANT = 'aiPhotoPaywall'

/** 渲染器入参(必须可序列化;JSX 在渲染器里生成,不放进 data) */
export interface AiPhotoPaywallData {
  /** 当前会员档位 vip_kind(0 = 非会员) */
  vipKind: number
  /** 已用积分(会员态副标题展示) */
  usedCredits: number
  /** AI照片王触发来源(web_click_page_workgroup_open_vip 埋点 string2,
   *  枚举: 我的积分弹窗 / 会话页面底部积分提示条 / 下载二级页面会员专属任务 / 照片地图入口;
   *  由 VipPaymentModal 的 sensorTracking 从 customContent.data 读出上报) */
  trackSource?: string
}

/** 强调样式:橙色加粗,用于横幅里的积分数字 */
const EM: React.CSSProperties = { color: '#ff7a00', fontWeight: 600 }

/** 横幅背景条(设计稿:米黄底圆角条 + 左侧橙色菱形点缀) */
const BANNER_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 8,
  background: '#fdf4e7',
  fontSize: 13,
  color: '#46403a',
  lineHeight: 1.5
}

/** 横幅左侧橙色菱形点缀 */
const BANNER_ICON: React.CSSProperties = {
  color: '#ff7a00',
  fontSize: 12,
  lineHeight: 1
}

/** vip_kind → 档位中文名(会员态副标题用) */
const TIER_NAME: Record<number, string> = {
  [CONFIG.VIP_KIND.VIP]: '基础会员',
  [CONFIG.VIP_KIND.VVIP]: '高级会员',
  [CONFIG.VIP_KIND.SVIP]: '超级会员',
  [CONFIG.VIP_KIND.EVIP]: '企业级会员'
}

/** tab 名下小字:带自有 className —— 加粗 + 选中 tab 变橙由 aiPhotoPaywallContent.scss
 *  以 `.tab-item.active .ai-photo-paywall-tab-note` 实现,只影响本业务注册的小字,
 *  不碰弹窗本体、不影响其他业务(如 AIClassifyFolder)的 tab 小字。 */
function tabNote(text: string): React.ReactNode {
  return <span className="ai-photo-paywall-tab-note">{text}</span>
}

/** 各档 tab 名下小字:每月赠送积分 */
const TAB_NOTES = {
  basic: tabNote('50万 积分/月'),
  advanced: tabNote('600万 积分/月'),
  super: tabNote('3000万 积分/月'),
  enterprise: tabNote('更多惊喜')
}

/** 单档横幅:米黄背景条 + 菱形点缀 + 「{档位} 获赠 {积分}[ · 含 照片地图] · 积分共享，永久有效」。
 *  withMap=true 的档(高级及以上)在积分后并列「照片地图」, 与积分同款橙色强调
 *  (产品: 照片地图权益在「获赠」横幅这块表达, 让用户知道买哪档会员能用)。 */
function cardBanner(
  tierName: string,
  credit: string,
  withMap: boolean
): React.ReactNode {
  return (
    <div style={BANNER_WRAP}>
      <span style={BANNER_ICON}>◆</span>
      <span>
        {tierName} 获赠 <span style={EM}>{credit}</span>
        {withMap && (
          <>
            {' · 含 '}
            <span style={EM}>照片地图</span>
          </>
        )}
        {' · 积分共享，永久有效'}
      </span>
    </div>
  )
}

/** 各档商品卡上方横幅(带背景条,设计走查 2026-06-10 李晓伟)。
 *  照片地图为高级会员及以上权益: advanced/super/enterprise 横幅并列展示, basic 不展示。 */
const CARD_BANNER = {
  basic: cardBanner('基础会员', '50万 积分/月', false),
  advanced: cardBanner('高级会员', '600万 积分/月', true),
  super: cardBanner('超级会员', '3000万 积分/月', true),
  enterprise: (
    <div style={BANNER_WRAP}>
      <span style={BANNER_ICON}>◆</span>
      <span>
        企业级会员 · 专属定制权益 · 含 <span style={EM}>照片地图</span> ·
        积分共享
      </span>
    </div>
  )
}

/** 积分数格式化:≥1 万显示「X万」,否则原数(带千分位) */
function formatCredits(n: number): string {
  if (n >= 10000) {
    const w = n / 10000
    return `${Number.isInteger(w) ? w : w.toFixed(1)}万`
  }
  return n.toLocaleString()
}

/**
 * 注册 AI 问照片开通弹窗的定制内容(模块加载时执行一次)。
 * 只要本文件被 import,注册即生效。
 */
registerPaymentContent(AI_PHOTO_PAYWALL_VARIANT, (d: AiPhotoPaywallData) => {
  const isNovip = !d.vipKind
  return {
    title: '开通会员，AI 问照片用到爽',
    subtitle: isNovip
      ? '一份会员费，全团队的 AI 助理 · 积分共享 · 永久有效'
      : `当前为${TIER_NAME[d.vipKind] || '会员'}，已用 ${formatCredits(
          d.usedCredits
        )}积分`,
    tabNotes: TAB_NOTES,
    cardBanner: CARD_BANNER
  }
})

/** 给调用处用:把数据包成 { variant, data } 传给 openVipByKind 第 4 参 customContent */
export function aiPhotoPaywallCustomContent(d: AiPhotoPaywallData) {
  return { variant: AI_PHOTO_PAYWALL_VARIANT, data: d }
}
